// components/DailyReport/ReportListModal.tsx
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  CircularProgress,
  Box,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { dailyReportService } from '../../services/dailyReport';
import { ReportListItem } from '../../types/dailyReport';

interface ReportListModalProps {
  open: boolean;
  constructionId: number | null;
  onClose: () => void;
  onEditReport: (id: number) => void;
}

export const ReportListModal: React.FC<ReportListModalProps> = ({
  open,
  constructionId,
  onClose,
  onEditReport,
}) => {
  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadReports = async () => {
      if (!open || !constructionId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const reportData = await dailyReportService.getByConstructionId(constructionId);
        setReports(reportData);
      } catch (err) {
        setError('日報一覧の読み込みに失敗しました');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadReports();
  }, [open, constructionId]);

  // 日付のフォーマット関数
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  // レポート項目をクリック時のハンドラー
  const handleReportClick = (e: React.MouseEvent, reportId?: number) => {
    e.stopPropagation(); // イベント伝播を停止
    if (!reportId) return;
    onEditReport(reportId);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="report-list-dialog-title"
      disableRestoreFocus // ConstructionFormと同様に設定
      keepMounted={false} // ダイアログが閉じられたらDOMから削除
    >
      <DialogTitle 
        id="report-list-dialog-title" 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: 1, 
          borderColor: 'divider',
          bgcolor: 'grey.50',
          px: 3,
          py: 2
        }}
      >
        <Typography variant="h6" component="span">日報一覧</Typography>
        <IconButton
          edge="end"
          aria-label="close"
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center" p={2}>
            {error}
          </Typography>
        ) : reports.length === 0 ? (
          <Typography align="center" p={2}>
            この工事に紐づく日報はありません
          </Typography>
        ) : (
          <List sx={{ p: 0 }}>
            {reports.map((report, index) => (
              <React.Fragment key={report.id}>
                {index > 0 && <Divider />}
                <Box
                  sx={{ 
                    py: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    }
                  }}
                  onClick={(e) => handleReportClick(e, report.id)}
                >
                  <Typography variant="subtitle1" component="div" fontWeight="bold">
                    {formatDate(report.date)}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" component="div" color="text.secondary">
                      担当者: {report.supervisor || '(担当者なし)'}
                    </Typography>
                    <Typography variant="body2" component="div" color="text.secondary">
                      工数: {report.manPower || '0'}人日
                    </Typography>
                    {report.totalAmount ? (
                      <Typography variant="body2" component="div" color="text.secondary">
                        金額: ¥{report.totalAmount.toLocaleString()} 
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};